// src/features/executive/account/widgets/TreatmentRevenueCard.tsx
//
// Raw catalog view: one slice per `operative_types` row that earned revenue
// from a patient in the selected period. No keyword folding — every distinct
// treatment type appears in the legend with its current/previous comparison.

import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip as ChartTooltip,
} from 'chart.js'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { useTreatmentRevenue } from '../hooks/use-treatment-revenue'
import { formatCurrency, formatVariance } from 'utils/executive.utils'
import { paletteColor } from 'features/executive/shared/utils/palette'
import { shouldUseBar, sortByValueDesc } from 'features/executive/shared/utils/chart-mode'
import CategoricalBar, { CategoricalBarRow } from 'features/executive/insight/widgets/CategoricalBar'

ChartJS.register(ArcElement, ChartTooltip, Legend)

export default function TreatmentRevenueCard() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useTreatmentRevenue()

  const isEmpty = !data || data.total === 0

  const chartData = useMemo(() => {
    if (!data) return null
    return {
      labels: data.categories.map((c) => c.name),
      datasets: [
        {
          data: data.categories.map((c) => c.amount),
          backgroundColor: data.categories.map((_, i) => paletteColor(i)),
        },
      ],
    }
  }, [data])

  const totalVariance =
    data && data.previousTotal != null && data.previousTotal !== 0
      ? (data.total - data.previousTotal) / Math.abs(data.previousTotal)
      : null

  const useBar = !isEmpty && shouldUseBar(data?.categories.length ?? 0)

  const barRows: CategoricalBarRow[] = data
    ? sortByValueDesc(
        data.categories.map((c, i) => ({
          key: String(c.id),
          label: c.name,
          count: c.amount,
          percent: c.percent,
          previousCount: c.previousAmount,
          color: paletteColor(i),
        })),
        (r) => r.count,
      )
    : []

  if (useBar) {
    const subtitle = `${t('EXECUTIVE.ACCOUNT.TREATMENT_REVENUE.CENTER_LABEL', 'รายได้หัตถการ')}: ${formatCurrency(
      data?.total ?? 0,
    )}${totalVariance != null ? ` · ${formatVariance(totalVariance)}` : ''}`
    return (
      <CategoricalBar
        title={t('EXECUTIVE.ACCOUNT.TREATMENT_REVENUE.TITLE', 'รายได้ตามหมวด Tx.')}
        titleTooltip={t('EXECUTIVE.ACCOUNT.TREATMENT_REVENUE.TOOLTIP')}
        subtitle={subtitle}
        rows={barRows}
        initialVisible={5}
        formatValue={(r) => `${formatCurrency(r.count)} (${r.percent.toFixed(0)}%)`}
        loading={loading}
        error={error}
        onRetry={refetch}
      />
    )
  }

  return (
    <WidgetCard
      title={t('EXECUTIVE.ACCOUNT.TREATMENT_REVENUE.TITLE', 'รายได้ตามหมวด Tx.')}
      titleTooltip={t('EXECUTIVE.ACCOUNT.TREATMENT_REVENUE.TOOLTIP')}
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      skeletonHeight={380}
    >
      {data && (
        <>
          <Box sx={{ height: 200, position: 'relative', mb: 2 }}>
            <Doughnut
              data={chartData as any}
              options={{
                maintainAspectRatio: false,
                responsive: true,
                cutout: '70%',
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) =>
                        `${ctx.label}: ${formatCurrency(Number(ctx.parsed))} (${(
                          (Number(ctx.parsed) / data.total) *
                          100
                        ).toFixed(1)}%)`,
                    },
                  },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t('EXECUTIVE.ACCOUNT.TREATMENT_REVENUE.CENTER_LABEL', 'รายได้หัตถการ')}
              </Typography>
              <Typography variant="h6">{formatCurrency(data.total)}</Typography>
              {totalVariance != null && (
                <Typography
                  variant="caption"
                  color={totalVariance >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatVariance(totalVariance)}
                </Typography>
              )}
            </Box>
          </Box>
          <Stack spacing={0.5} sx={{ maxHeight: 220, overflowY: 'auto', pr: 0.5 }}>
            {data.categories.map((c, i) => {
              const variance =
                c.previousAmount != null && c.previousAmount !== 0
                  ? (c.amount - c.previousAmount) / Math.abs(c.previousAmount)
                  : null
              return (
                <Box
                  key={c.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: paletteColor(i),
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={c.name}
                  >
                    {c.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(c.amount)} ({c.percent.toFixed(0)}%)
                  </Typography>
                  {variance != null && (
                    <Typography
                      variant="caption"
                      color={variance >= 0 ? 'success.main' : 'error.main'}
                      sx={{ minWidth: 56, textAlign: 'right' }}
                    >
                      {formatVariance(variance)}
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Stack>
        </>
      )}
    </WidgetCard>
  )
}
